<template>
  <div>
    <div class="card-body">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <div class="form-heading">
          {{ post ? $t('posts.editPost') : $t('posts.newPost') }}
        </div>
        <CloseButton @click.native="$emit('cancel')" />
      </div>

      <!-- Channel -->
      <BFormGroup :label="$t('posts.channels')">
        <BFormSelect
          v-model="form.channelId"
          :options="channelSelectOptions"
        />
      </BFormGroup>

      <div
        v-if="isSystemChannel"
        class="system-news-info mb-3"
      >
        <i class="far fa-circle-info mr-2" />
        {{ $t('news.form.info') }}
      </div>

      <!-- Title -->
      <BFormGroup :label="$t('posts.title')">
        <BFormInput
          v-model="form.title"
          :placeholder="$t('posts.titlePlaceholder')"
        />
      </BFormGroup>

      <template v-if="isSystemChannel">
        <BFormGroup :label="$t('news.form.topic')">
          <div class="d-flex flex-wrap">
            <button
              v-for="topic in topics"
              :key="topic"
              type="button"
              class="btn btn-sm topic-chip mr-2 mb-2"
              :class="announcement.topic === topic ? 'btn-outline-primary active' : 'btn-outline-secondary'"
              @click="announcement.topic = topic"
            >
              {{ $t(`news.topics.${topic}`) }}
            </button>
          </div>
          <div class="small text-secondary">
            {{ $t('news.form.topicHint') }}
          </div>
        </BFormGroup>

        <div class="row">
          <BFormGroup
            :label="$t('news.form.category')"
            class="col-md-6"
          >
            <BFormSelect
              v-model="announcement.category"
              :options="categoryOptions"
            />
          </BFormGroup>
          <BFormGroup
            :label="$t('news.form.publishAt')"
            class="col-md-6"
          >
            <!-- The timestamp is part of the post's key, so it cannot change on edit. -->
            <BFormInput
              v-model="publishAt"
              type="datetime-local"
              :disabled="!!post"
            />
          </BFormGroup>
        </div>

        <BFormGroup :label="$t('news.form.summary')">
          <BFormTextarea
            v-model="announcement.summary"
            rows="2"
            maxlength="160"
          />
          <div class="small text-secondary">
            {{ $t('news.form.summaryHint', { count: announcement.summary.length }) }}
          </div>
        </BFormGroup>
      </template>

      <!-- Rich text body -->
      <BFormGroup :label="$t('posts.body')">
        <RichTextEditor
          v-model="form.body"
          :placeholder="$t('posts.bodyPlaceholder')"
          :enable-links-and-images="true"
        />
      </BFormGroup>

      <!-- Media files -->
      <BFormGroup :label="$t('posts.media')">
        <div
          v-if="form.media.length > 0"
          class="media-list mb-2"
        >
          <div
            v-for="(mp, idx) in form.media"
            :key="idx"
            class="media-item d-flex align-items-center mb-1"
            :class="{ 'is-thumbnail': form.thumbnail?.path === mp.path }"
          >
            <img
              v-if="isImage(mp.path)"
              :src="mp.url"
              class="media-thumb mr-2"
            >
            <i
              v-else
              class="far fa-file mr-2"
            />
            <!-- <div class="small text-truncate flex-grow-1">
              {{ mediaFileName(mp.path) }}
            </div> -->
            <button
              v-if="isImage(mp.path)"
              class="btn btn-sm ml-1"
              :class="form.thumbnail?.path === mp.path ? 'btn-primary' : 'btn-outline-secondary'"
              :title="$t('posts.setAsThumbnail')"
              @click="toggleThumbnail(mp)"
            >
              <i class="far fa-image mr-1" />
              Ustaw jako miniaturkę
            </button>
            <button
              class="btn btn-sm btn-outline-danger ml-1"
              @click="removeMedia(idx)"
            >
              <i class="fas fa-times mr-1" />
              Usuń
            </button>
          </div>
        </div>

        <div
          v-if="mediaUploading"
          class="mb-2"
        >
          <Loader size="20px" />
        </div>

        <FileUpload @upload="onFileAdded" />
      </BFormGroup>

      <BFormGroup
        v-if="isSystemChannel"
        :label="$t('news.form.options')"
      >
        <BFormCheckbox
          v-model="announcement.popup"
          switch
        >
          {{ $t('news.form.popup') }}
        </BFormCheckbox>
        <div class="small text-secondary mb-2 ml-4">
          {{ $t('news.form.popupHint') }}
        </div>
        <BFormCheckbox
          v-model="announcement.pinned"
          switch
        >
          {{ $t('news.form.pinned') }}
        </BFormCheckbox>
        <div class="small text-secondary ml-4">
          {{ $t('news.form.pinnedHint') }}
        </div>
      </BFormGroup>

      <!-- Actions -->
      <div class="d-flex justify-content-end">
        <button
          class="btn btn-secondary mr-2"
          @click="$emit('cancel')"
        >
          {{ $t('general.cancel') }}
        </button>
        <button
          v-if="isSystemChannel"
          class="btn btn-outline-primary mr-2"
          :disabled="savingPost || !form.title"
          @click="savePost(true)"
        >
          {{ $t('news.form.saveDraft') }}
        </button>
        <button
          class="btn btn-primary"
          :disabled="savingPost || !form.title"
          @click="savePost(false)"
        >
          <span v-if="!savingPost">
            <i class="fas fa-check mr-1" />
            {{ $t('general.save') }}
          </span>
          <Loader
            v-else
            color="#fff"
            size="15px"
          />
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from '@/api/axios';
import FileUpload from '@/components/FileUpload';
import RichTextEditor from '@/components/editor/RichTextEditor';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';

export default {
  name: 'PostForm',
  props: {
    post: {
      type: Object,
      default: null,
    },
    channelId: {
      type: String,
      required: true,
    },
    channelGroups: {
      type: Array,
      default: () => [],
    },
    // Set only for the organisation allowed to publish system announcements.
    systemChannelId: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      savingPost: false,
      mediaUploading: false,
      form: {
        id: this.post ? this.post.id : uuidv4(),
        channelId: this.post ? (this.post.channelId || this.channelId) : this.channelId,
        title: this.post ? (this.post.title || '') : '',
        body: this.post ? (this.post.body || '') : '',
        thumbnail: this.post ? (this.post.thumbnail || null) : null,
        media: this.post ? [...(this.post.media || [])] : [],
      },
      announcement: {
        topic: this.post?.announcement?.topic || 'General',
        category: this.post?.announcement?.category || 'New',
        summary: this.post?.announcement?.summary || '',
        popup: this.post?.announcement?.popup || false,
        pinned: this.post?.announcement?.pinned || false,
      },
      publishAt: moment(this.post?.timestamp || undefined).format('YYYY-MM-DDTHH:mm'),
      topics: ['General', 'Billing', 'Catering', 'Courses', 'Shop'],
    };
  },
  components: {
    RichTextEditor,
    FileUpload,
  },
  computed: {
    isSystemChannel() {
      return !!this.systemChannelId && this.form.channelId === this.systemChannelId;
    },
    categoryOptions() {
      return ['New', 'Improvement', 'Change', 'Important'].map((value) => ({
        value,
        text: this.$t(`news.categories.${value}`),
      }));
    },
    channelSelectOptions() {
      const options = [
        {
          label: this.$t('posts.publicChannel'),
          options: [
            {
              value: 'public',
              text: this.$t('posts.publicChannel'),
            },
          ],
        },
      ];

      if (this.systemChannelId) {
        options.push({
          label: this.$t('news.systemGroup'),
          options: [
            {
              value: this.systemChannelId,
              text: this.$t('news.systemChannel'),
            },
          ],
        });
      }

      this.channelGroups.forEach((group) => {
        if (!group || !Array.isArray(group.channels) || group.channels.length === 0) {
          return;
        }

        options.push({
          label: group.catalogName,
          options: group.channels.map((channel) => ({
            value: channel.id,
            text: channel.name,
          })),
        });
      });

      return options;
    },
  },
  watch: {
    channelId(nextChannelId) {
      if (!this.post && this.form.channelId !== nextChannelId) {
        this.form.channelId = nextChannelId;
      }
    },
  },
  methods: {
    mediaFileName(path) {
      if (!path) return '';
      return path.split('/').pop();
    },
    isImage(path) {
      if (!path) return false;
      return /\.(jpe?g|png|gif|webp|svg|bmp)$/i.test(path);
    },
    toggleThumbnail(media) {
      if (this.form.thumbnail?.path === media.path) {
        this.form.thumbnail = null;
      } else {
        this.form.thumbnail = media;
      }
    },
    async onFileAdded(file) {
      if (!file) return;

      this.mediaUploading = true;
      try {
        const formData = new FormData();
        formData.append('File', file);
        formData.append('PostId', this.form.id);

        const { data } = await axios.post('messages/posts/media', formData);
        this.form.media.push({
          path: data.key,
          url: data.url,
        });

        // Auto-select as thumbnail if it's the first image and no thumbnail is set
        if (!(this.form.thumbnail || this.form.thumbnail?.path) && this.isImage(data.key)) {
          this.form.thumbnail = {
            path: data.key,
            url: data.url,
          };
        }
      } catch {
        // upload error
      } finally {
        this.mediaUploading = false;
      }
    },
    removeMedia(idx) {
      const removed = this.form.media[idx];
      this.form.media.splice(idx, 1);
      if (this.form.thumbnail === removed) {
        // Auto-select next available image as thumbnail, or clear
        this.form.thumbnail = this.form.media.find((m) => this.isImage(m.path)) || null;
      }
    },
    async savePost(draft) {
      this.savingPost = true;
      const body = this.form.body || '';

      // For system announcements the post timestamp is the publication date; a future
      // date schedules the announcement. Other posts keep their original timestamp.
      let timestamp = this.post?.timestamp || new Date().toISOString();
      if (!this.post && this.isSystemChannel && this.publishAt) {
        timestamp = moment(this.publishAt).toISOString();
      }

      const payload = {
        id: this.form.id,
        timestamp,
        title: this.form.title,
        body,
        channelId: this.form.channelId,
        oldChannelId: this.post ? this.post.channelId : undefined,
        thumbnailPath: this.form.thumbnail?.path || '',
        mediaPaths: this.form.media.map((m) => m.path),
        announcement: this.isSystemChannel
          ? { ...this.announcement, draft: !!draft }
          : undefined,
      };

      try {
        if (this.post) {
          await axios.put('messages/posts', payload);
        } else {
          await axios.post('messages/posts', payload);
        }
        this.$emit('saved');
      } catch {
        // save error
      } finally {
        this.savingPost = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/styles/vars';

.system-news-info {
  background: rgba($blue, 0.08);
  color: darken($blue, 15);
  border-radius: 10px;
  padding: 10px 14px;
  font-size: 13px;
}

.topic-chip {
  border-radius: 16px;
}

.form-heading {
  font-weight: 600;
  font-size: 16px;
}

.media-list {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 8px;
}

.media-item {
  padding: 4px 6px;
  border-radius: 6px;
  transition: background 150ms;

  &:hover {
    background: #f8f9fa;
  }

  &.is-thumbnail {
    background: rgba($blue, 0.06);
    border-left: 3px solid $blue;
  }
}

.media-thumb {
  width: 50px;
  height: 70px;
  object-fit: cover;
  border-radius: 4px;
}
</style>
